import { useEffect, useState } from "react";
import api, { fmtIDR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Globe, ArrowLeftRight } from "lucide-react";

export default function GlobalStock() {
  const { isPriv, user } = useAuth();
  const [q, setQ] = useState("");
  const [data, setData] = useState({ products: [], spareparts: [], branches: [] });
  const [loading, setLoading] = useState(true);

  const load = (query = "") => {
    setLoading(true);
    api.get("/stock/global", { params: query ? { q: query } : {} })
      .then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const requestTransfer = async (item, type) => {
    if (!user?.branch_id && !isPriv) return toast.error("Cabang tidak diketahui");
    const toBranch = user?.branch_id || (data.branches[0] && data.branches[0].id);
    if (item.branch_id === toBranch) return toast.error("Item sudah di cabang Anda");
    try {
      await api.post("/transfers", {
        item_type: type, item_id: item.id, item_name: item.name, qty: 1,
        from_branch: item.branch_id, to_branch: toBranch,
      });
      toast.success("Permintaan transfer dikirim");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const Table = ({ title, list, type }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-slate-100 font-medium text-slate-800">{title} ({list.length})</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Nama", "Cabang", "Stok", "Harga Jual", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 ? <tr><td colSpan={5}><Empty text="Tidak ditemukan" /></td></tr>
              : list.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200">{p.branch_name}</span></td>
                  <td className={`px-4 py-3 font-mono-num ${p.stock <= 0 ? "text-red-600 font-semibold" : ""}`}>{p.stock}{p.stock <= 0 && " (habis)"}</td>
                  <td className="px-4 py-3 font-mono-num">{fmtIDR(p.sell_price)}</td>
                  <td className="px-4 py-3 text-right">
                    {p.stock > 0 && p.branch_id !== user?.branch_id && (
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => requestTransfer(p, type)} data-testid={`req-transfer-${p.id}`}>
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Minta Transfer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Cek Stok Global" subtitle="Ketersediaan stok di seluruh cabang secara real-time" />
      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / sparepart di semua cabang..." className="pl-9" data-testid="global-search" />
      </div>
      {loading ? <Loading /> : (
        <>
          <div className="mb-3 flex items-center gap-2 text-sm text-sky-700"><Globe className="w-4 h-4" /> {data.branches.length} cabang terpantau</div>
          <Table title="Produk / HP" list={data.products} type="product" />
          <Table title="Sparepart" list={data.spareparts} type="sparepart" />
        </>
      )}
    </div>
  );
}
